<!-- Section Header with Sentiment + auto-save status -->
<ind-section-header
  [viewData]="headerModel()"
  (onControlClick)="onHeaderControl($event)">
</ind-section-header>

@if (pkmState.isCompactMode()) {
  <!-- ═══════════════════════════════════════════════════════════
       COMPACT MODE — all products shown inline as checklist
       (config preferred products === user's available products)
       ═══════════════════════════════════════════════════════════ -->
  <div class="pkm-compact-container">
    <ion-reorder-group
      class="pkm-compact-list"
      [disabled]="pkmState.isReadOnly()"
      (ionItemReorder)="onCompactReorder($event.detail)">

      @for (product of pkmState.compactDisplayProducts(); track product.ID) {
        <ion-item lines="none" class="pkm-compact-item">
          <div class="pkm-compact-card"
            [class.pkm-compact-card--selected]="product.isSelected || product.isAutoSelected">

            <div class="pkm-compact-header">
              <!-- Reorder grip — only for selected products -->
              @if ((product.isSelected || product.isAutoSelected) && !pkmState.isReadOnly()) {
                <ion-reorder class="pkm-reorder-handle">
                  <svg class="pkm-grip-dots" width="10" height="14" viewBox="0 0 10 14" fill="currentColor">
                    <circle cx="3" cy="2" r="1.4"/>
                    <circle cx="7" cy="2" r="1.4"/>
                    <circle cx="3" cy="7" r="1.4"/>
                    <circle cx="7" cy="7" r="1.4"/>
                    <circle cx="3" cy="12" r="1.4"/>
                    <circle cx="7" cy="12" r="1.4"/>
                  </svg>
                </ion-reorder>
              }

              <!-- Checkbox — circle, gray if auto-selected or has auto KMs -->
              <div class="pkm-compact-checkbox"
                [class.pkm-compact-checkbox--checked]="(product.isSelected || product.isAutoSelected) && !product.isAutoSelected && !hasAutoKm(product)"
                [class.pkm-compact-checkbox--auto]="product.isAutoSelected || hasAutoKm(product)"
                (click)="onCompactProductToggle(product); $event.stopPropagation()">
                @if (product.isSelected || product.isAutoSelected || hasAutoKm(product)) {
                  <svg width="8" height="6" viewBox="0 0 8 6" fill="none">
                    <path d="M1 3L3 5L7 1" stroke="#fff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                }
              </div>

              <span class="pkm-compact-product-name"
                (click)="onCompactProductToggle(product); $event.stopPropagation()">
                {{ product.name }}
              </span>
            </div>

            <!-- KM pills — sorted alphabetically -->
            @if (product.keyMessages?.length) {
              <div class="pkm-compact-km-row">
                @for (km of sortedKeyMessages(product); track km.ID) {
                  <span class="pkm-pill"
                    [class.pkm-pill-selected]="km.isSelected && !km.isAutoSelected"
                    [class.pkm-pill-auto]="km.isAutoSelected"
                    [attr.disabled]="pkmState.isReadOnly() || null"
                    (click)="onCompactKmToggle(product, km)">
                    {{ km.name }}
                  </span>
                }
              </div>
            }
          </div>
        </ion-item>
      }
    </ion-reorder-group>
  </div>

} @else {
  <!-- ═══════════════════════════════════════════════════════════
       SEARCH MODE — IAO-style search with body-appended dropdown
       Auto-save on every interaction (no Save/Cancel buttons)
       ═══════════════════════════════════════════════════════════ -->

  <!-- IAO-style Inline Product Search -->
  @if (!pkmState.isReadOnly()) {
    <div class="pkm-inline-search">
      <div #productSearchWrap class="pkm-product-search-wrap">
        <ion-icon name="search-outline" class="pkm-search-icon"></ion-icon>
        <input type="text"
          class="pkm-product-search-input"
          [placeholder]="'SEARCH_PRODUCT_TO_ADD' | translate"
          [value]="productSearchText"
          (input)="onSearchInput($event.target.value)"
          (focus)="onSearchFocus()"
          (blur)="onSearchBlur()" />
        @if (productSearchText) {
          <button class="pkm-search-cancel-btn" (mousedown)="clearSearchText(); $event.preventDefault()">
            <ion-icon name="close-outline"></ion-icon>
          </button>
        }
      </div>
    </div>
  }

  <!-- Dropdown template — rendered into body-appended container via TemplateRef -->
  <ng-template #dropdownTpl>
    <div class="pkm-dropdown">

      <!-- Preferred Products (sticky) -->
      @if (filteredPreferred().length) {
        <div class="pkm-dropdown-preferred">
          <div class="pkm-dropdown-preferred-scroll">
            @for (brand of filteredPreferred(); track brand.ID) {
              <div class="pkm-pref-chip"
                [class.pkm-pref-chip--auto]="brand.isAutoSelected"
                [attr.title]="brand.name"
                (mousedown)="onPreferredClick(brand, $event)">
                <div class="pkm-pref-avatar-wrap">
                  <div class="pkm-pref-avatar"
                    [class.pkm-pref-avatar--selected]="brand.isSelected || brand.isAutoSelected"
                    [class.pkm-pref-avatar--auto]="brand.isAutoSelected || hasAutoKm(brand)">
                    {{ getInitials(brand.name) }}
                  </div>
                  @if ((brand.isSelected || brand.isAutoSelected) && !brand.isAutoSelected && !hasAutoKm(brand)) {
                    <div class="pkm-pref-tick pkm-pref-tick--selected">
                      <svg width="8" height="6" viewBox="0 0 8 6" fill="none">
                        <path d="M1 3L3 5L7 1" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </div>
                  }
                  @if (brand.isAutoSelected || hasAutoKm(brand)) {
                    <div class="pkm-pref-tick pkm-pref-tick--auto">
                      <svg width="8" height="6" viewBox="0 0 8 6" fill="none">
                        <path d="M1 3L3 5L7 1" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </div>
                  }
                </div>
                <span class="pkm-pref-label">{{ brand.name }}</span>
              </div>
            }
          </div>
        </div>
      }

      <!-- Product list -->
      @if (!pkmState.availableForSearch().length) {
        <div class="pkm-dropdown-empty">{{ 'NO_RESULTS' | translate }}</div>
      } @else {
        @for (brand of pkmState.availableForSearch(); track brand.ID) {
          <div class="pkm-dropdown-product"
            [attr.data-brand-id]="brand.ID"
            [class.pkm-dropdown-product--checked]="brand.isSelected || brand.isAutoSelected"
            [class.pkm-dropdown-product--auto]="brand.isAutoSelected">

            <div class="pkm-dropdown-product-row">
              <div class="pkm-dropdown-checkbox"
                [class.pkm-dropdown-checkbox--checked]="(brand.isSelected || brand.isAutoSelected) && !brand.isAutoSelected"
                [class.pkm-dropdown-checkbox--auto]="brand.isAutoSelected"
                (mousedown)="onDropdownProductClick(brand, $event)">
                @if (brand.isSelected || brand.isAutoSelected) {
                  <svg width="8" height="6" viewBox="0 0 8 6" fill="none">
                    <path d="M1 3L3 5L7 1" stroke="#fff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                }
              </div>
              <span class="pkm-dropdown-product-name"
                (mousedown)="onDropdownProductClick(brand, $event)">{{ brand.name }}</span>
            </div>

            @if (brand.keyMessages?.length) {
              <div class="pkm-dropdown-km-row">
                @for (km of sortedKeyMessages(brand); track km.ID) {
                  <span class="pkm-dropdown-km"
                    [class.pkm-dropdown-km--active]="km.isSelected && !km.isAutoSelected"
                    [class.pkm-dropdown-km--auto]="km.isAutoSelected"
                    (mousedown)="onDropdownKmClick(brand, km, $event)">
                    {{ km.name }}
                  </span>
                }
              </div>
            }
          </div>
        }
      }
    </div>
  </ng-template>

  <!-- Product Cards List (search mode — selected products) -->
  @if (pkmState.displayProducts().length) {
    <div class="pkm-scroll-container">
      <ion-reorder-group
        class="pkm-cards-list"
        [disabled]="pkmState.isReadOnly()"
        (ionItemReorder)="onReorder($event.detail)">

        @for (product of pkmState.displayProducts(); track product.ID) {
          <ion-item lines="none" class="pkm-card-item">
            <div class="pkm-card">
              <div class="pkm-card-header">
                @if (!pkmState.isReadOnly()) {
                  <ion-reorder class="pkm-reorder-handle">
                    <svg class="pkm-grip-dots" width="10" height="14" viewBox="0 0 10 14" fill="currentColor">
                      <circle cx="3" cy="2" r="1.4"/>
                      <circle cx="7" cy="2" r="1.4"/>
                      <circle cx="3" cy="7" r="1.4"/>
                      <circle cx="7" cy="7" r="1.4"/>
                      <circle cx="3" cy="12" r="1.4"/>
                      <circle cx="7" cy="12" r="1.4"/>
                    </svg>
                  </ion-reorder>
                }
                <span class="pkm-product-name">{{ product.name }}</span>
                @if (product.isSelected && !product.isAutoSelected && !pkmState.isReadOnly()) {
                  <button class="pkm-remove-btn" (click)="removeProduct(product)">
                    <ion-icon name="close-outline"></ion-icon>
                  </button>
                }
              </div>

              @if (product.keyMessages?.length) {
                <div class="pkm-km-row">
                  @for (km of product.keyMessages; track km.ID) {
                    <span
                      class="pkm-pill"
                      [class.pkm-pill-selected]="km.isSelected && !km.isAutoSelected"
                      [class.pkm-pill-auto]="km.isAutoSelected"
                      [attr.disabled]="pkmState.isReadOnly() || null"
                      (click)="toggleKeyMessage(product, km)"
                      [id]="product.ID + '-' + km.ID">
                      {{ km.name }}
                    </span>
                  }
                </div>
              }
            </div>
          </ion-item>
        }
      </ion-reorder-group>
    </div>
  }
}
